import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useClients } from '../contexts/ClientContext';
import { clientsAPI } from '../services/api';
import {
  parseClientIdParam,
  resolveScopedClientId,
} from '../utils/lastSelectedClientStorage';
import {
  CLIENT_SCOPED_VARIANTS,
  resolveClientScopedVariant,
} from '../utils/clientReadinessGate';

/**
 * Determines whether a client-scoped page should show an activation empty state.
 * Uses GET /clients/{id}/readiness — same source as ClientReadinessPanel.
 */
export function useClientScopedGate({
  clientId: explicitClientId,
  requirePosts = true,
  acceptGsc = false,
} = {}) {
  const [searchParams] = useSearchParams();
  const {
    clients,
    loaded,
    loading: clientsLoading,
    lastSelectedClientId,
    setLastSelectedClientId,
  } = useClients();
  const [readiness, setReadiness] = useState(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  const [readinessFailed, setReadinessFailed] = useState(false);

  const urlClientId = parseClientIdParam(searchParams.get('client_id'));

  useEffect(() => {
    if (!loaded || urlClientId == null) return;
    if (clients.some((c) => c.id === urlClientId)) {
      setLastSelectedClientId(urlClientId);
    }
  }, [loaded, urlClientId, clients, setLastSelectedClientId]);

  const clientId = useMemo(() => {
    if (explicitClientId != null && explicitClientId !== '') {
      return parseClientIdParam(explicitClientId);
    }
    return resolveScopedClientId({
      clients,
      urlClientId,
      persistedClientId: lastSelectedClientId,
    });
  }, [explicitClientId, clients, urlClientId, lastSelectedClientId]);

  useEffect(() => {
    if (!loaded || clients.length === 0 || !clientId) {
      setReadiness(null);
      setReadinessFailed(false);
      setReadinessLoading(false);
      return undefined;
    }

    let cancelled = false;
    setReadinessLoading(true);
    setReadinessFailed(false);

    clientsAPI
      .getClientReadiness(clientId)
      .then((data) => {
        if (!cancelled) setReadiness(data);
      })
      .catch(() => {
        if (!cancelled) {
          setReadiness(null);
          setReadinessFailed(true);
        }
      })
      .finally(() => {
        if (!cancelled) setReadinessLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [loaded, clients.length, clientId]);

  const variant = useMemo(() => {
    if (!loaded) return undefined;
    if (clients.length === 0) return CLIENT_SCOPED_VARIANTS.NO_CLIENTS;
    if (!clientId) return undefined;
    if (readinessLoading && !readiness && !readinessFailed) return undefined;
    if (readinessFailed) return null;
    return resolveClientScopedVariant(readiness, { requirePosts, acceptGsc });
  }, [
    loaded,
    clients.length,
    clientId,
    readiness,
    readinessLoading,
    readinessFailed,
    requirePosts,
    acceptGsc,
  ]);

  const loading =
    !loaded ||
    clientsLoading ||
    (clients.length > 0 &&
      clientId != null &&
      readinessLoading &&
      !readiness &&
      !readinessFailed);

  return {
    loading,
    variant: variant === undefined ? null : variant,
    blocked: variant != null && variant !== null,
    clientId,
    readiness,
  };
}
